import { Card, CardContent, Badge } from '@makanly/ui-kit';
import { Activity, Clock } from 'lucide-react';
import type { ServiceStatus } from '../types';
import { formatResponseTime, getBadgeVariant } from '../types';

interface StatusCardProps {
  service: ServiceStatus;
  showAnimation?: boolean;
}

export function StatusCard({ service, showAnimation = false }: StatusCardProps) {
  const badgeVariant = getBadgeVariant(service.status);
  const isHealthy = service.status === 'healthy';
  const isUnhealthy = service.status === 'unhealthy';

  const lastCheckedTime = service.lastChecked
    ? new Date(service.lastChecked).toLocaleTimeString()
    : '-';

  return (
    <Card
      className={`
        transition-all duration-300 hover:shadow-card-hover
        ${showAnimation ? 'animate-status-change' : ''}
        ${isUnhealthy ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30' : ''}
      `}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-text-primary truncate">{service.name}</h3>
              <Badge variant={badgeVariant} className="shrink-0">
                {service.status}
              </Badge>
            </div>

            <div className="flex items-center gap-4 text-sm text-text-secondary">
              <span className="font-mono">:{service.port}</span>

              {service.responseTime !== null && (
                <span className="flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  {formatResponseTime(service.responseTime)}
                </span>
              )}
            </div>

            {service.error && (
              <p
                className="mt-2 text-xs text-red-600 dark:text-red-400 truncate"
                title={service.error}
              >
                {service.error}
              </p>
            )}
          </div>

          <div
            className={`
              w-3 h-3 rounded-full shrink-0 mt-1
              ${isHealthy ? 'bg-green-500' : ''}
              ${isUnhealthy ? 'bg-red-500 animate-pulse' : ''}
              ${service.status === 'checking' ? 'bg-gray-400 animate-pulse' : ''}
            `}
          />
        </div>

        <div className="flex items-center gap-1 mt-3 text-xs text-text-secondary">
          <Clock className="w-3 h-3" />
          <span>Last checked: {lastCheckedTime}</span>
        </div>
      </CardContent>
    </Card>
  );
}
